import axios from 'axios';
import { AuthorizationService } from '../auth';
import { handleServiceError } from '@utils';
import { mapCategoryTree } from './mapper';
import {
  CategoriesWithRecent,
  Category,
  RecentlyViewedCategory,
} from './interfaces';
import { isFeatureEnabled, FEATURES } from '../../utils/featureFlags';
import { API_HOST_PROXY } from '../../constants/api';

interface CustomFieldRowType {
  name: string; value: string[]
}

const removeTenant = (category: Category, selectedTenantKey: string): void => {
  if (category.parent) {
    if (category.parent.key === selectedTenantKey) {
      delete category.parent;
    } else {
      removeTenant(category.parent, selectedTenantKey);
    }
  }
};

export class CategoryService extends AuthorizationService {
  static readonly serviceName: string = 'CategoryService';

  public async getAllCategories(tenantId?: string): Promise<Category[]> {
    try {
      const { data: { data: { categories } } } = await axios.get(`${API_HOST_PROXY}/categories`, {
        headers: {
          ...(tenantId && isFeatureEnabled(FEATURES.MULTI_TENANCY) ? { 'volcano-tenant-id': tenantId } : {})
        }
      });

      return categories.results;
    } catch (err) {
      handleServiceError(err);

      return [];
    }
  }

  public async getCategoriesTree(tenantId?: string): Promise<Category[]> {
    const categories = await this.getAllCategories(tenantId ?? 'tenant-default');

    return mapCategoryTree(categories);
  }

  public async getCategoriesWithRecent(tenantId?: string): Promise<CategoriesWithRecent> {
    try {
      const { data } = await axios.get(`${API_HOST_PROXY}/categories`, {
        headers: {
          ...(tenantId && isFeatureEnabled(FEATURES.MULTI_TENANCY) ? { 'volcano-tenant-id': tenantId } : {})
        }
      });

      const flatCategories: Category[] = data?.data?.categories?.results ?? [];
      const recentlyViewedCategories: RecentlyViewedCategory[] = Array.isArray(data?.recentlyViewedCategories)
        ? data.recentlyViewedCategories
        : [];

      return {
        categories: mapCategoryTree(flatCategories),
        recentlyViewedCategories
      };
    } catch (err) {
      handleServiceError(err);

      return { categories: [], recentlyViewedCategories: [] };
    }
  }

  public async getCategoryData(
    key?: string,
    ssr?: boolean,
    selectedTenantKey?: string
  ): Promise<Category | null> {
    try {
      const url = new URL(`${API_HOST_PROXY}/categories/search`);
      url.searchParams.set('includeChildren', 'true');
      if (ssr) url.searchParams.set('ssr', 'true');
      if (key) url.searchParams.set('key', key);
      url.searchParams.set('parentDepth', '5');

      const { data: category } = await axios.get(url.toString());

      if (isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
        removeTenant(category, selectedTenantKey);
      }

      let facetsExpression: CustomFieldRowType | null = null;
      let facetsLabel: CustomFieldRowType | null = null;

      const getCustomFieldValues = (fields: CustomFieldRowType[], fieldName: string): CustomFieldRowType => fields
        .find(field => field.name === fieldName);

      /**
        * Check is current category has "custom" facets,
        * if it has, reads facets from custom,
        * if not, reads facets from parent(s) recursively
        * - facets come as array in "customFieldsRaw",
        * - order (e.g. Expression[0] then Label[1]) is not guaranteed,
        * - so read by "name" property
        * - response example
        * [
        *   { name: 'facetExpression', value: [ 'brand', 'size' ] },
        *   { name: 'facetLabel', value: [ 'Brand', 'Size' ] }
        * ]
      */

      if (category.custom) {
        facetsExpression = getCustomFieldValues(category.custom.customFieldsRaw, 'facetExpression');
        facetsLabel = getCustomFieldValues(category.custom.customFieldsRaw, 'facetLabel');
      } else {
        let { parent } = category;

        while (parent) {
          if (parent.custom) {
            facetsExpression = getCustomFieldValues(parent.custom.customFieldsRaw, 'facetExpression');
            facetsLabel = getCustomFieldValues(parent.custom.customFieldsRaw, 'facetLabel');
            break;
          } else {
            ({ parent } = parent);
          }
        }
      }

      category.facets = [];
      facetsExpression?.value.forEach(
        (facetsExpression: string, index: number) => category.facets.push(
          { facet: facetsExpression, label: facetsLabel?.value[index] ?? '' }
        )
      );

      return category;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }
}
